import {
  Col,
  Icon,
  Input,
  type InputHandle,
  type Origin,
  originOf,
  Tappable,
  Text,
  useTheme,
  useTransition,
} from "@rally/ui-kit";
import { useEffect, useRef, useState } from "react";
import { say } from "../../localization/say.ts";
import { AppAttachments, type AppChoice } from "../app/app-attachments.tsx";
import { Attached } from "../attachment/attached.tsx";
import type { Picked } from "../attachment/attaching.ts";
import { matchingTags, type Offer, offersFrom, type Taggable, withTag } from "../message/tags.ts";
import { isTouchPointer, rightButton } from "../panel/right-button.ts";
import { COMPOSER_BORDER, COMPOSER_INSET } from "../reading.ts";
import { browserDictation } from "./browser-dictation.ts";
import { COMPOSER_ROW, COMPOSER_TALLEST, composerFieldHeight, whatTheKeyDoes } from "./composer-state.ts";
import { type Dictating, type Dictation, pressedDictation, QUIET, withDictated } from "./dictation.ts";
import { attemptForDraft, draftFor, forgetDraftAttempt, keepDraft, keepDraftAttempt } from "./draft.ts";
import { useDraftOwner } from "./draft-owner.tsx";
import { MentionList, rowName } from "./mention-list.tsx";
import { type Candidate, whatIsBeingTyped } from "./mentions.ts";
import { Microphone } from "./microphone.tsx";
import { SendButton } from "./send-button.tsx";
import { type SubmissionAttempt, submissionIdentifier, submissionStillOwnsDraft } from "./submission.ts";

export type SubmissionIdentity = (payload: unknown) => string;

export function Composer({
  conversation,
  onSay,
  onDraft,
  onTyped,
  onStopped,
  busy,
  who = [],
  onAdd,
  picked = [],
  onPick,
  onDrop,
  files = [],
  links = [],
  apps = [],
  placeholder,
  draftKey,
  testID = "composer",
  replyContext,
  error,
  autoFocus = false,
  focusSignal,
  dictation,
}: {
  conversation: string;

  draftKey?: string;

  placeholder?: string;
  testID?: string;

  dictation?: Dictation;

  replyContext?: { said: string; onClose: () => void };

  error?: string | null;
  autoFocus?: boolean;

  focusSignal?: number;
  onSay: (
    text: string,
    from: Origin | null,
    identify: SubmissionIdentity,
    apps: readonly string[],
  ) => Promise<boolean>;

  onDraft?: (text: string) => void;

  onTyped?: () => void;

  onStopped?: () => void;
  busy?: boolean;

  who?: readonly Candidate[];

  files?: readonly Taggable[];

  links?: readonly string[];
  apps?: readonly AppChoice[];

  onAdd?: (who: Candidate) => Promise<{ added: true } | { added: false; why: string }>;

  picked?: Picked[];

  onPick?: (files: File[]) => void;

  onDrop?: (key: string) => void;
}) {
  const theme = useTheme();
  const grow = useTransition("height", "fast");
  const owner = useDraftOwner();
  const shelf = `${owner}:${draftKey ?? conversation}`;

  const [text, setText] = useState(() => draftFor(shelf));
  const latest = useRef(text);
  latest.current = text;
  const revision = useRef(0);
  const submission = useRef<SubmissionAttempt | null>(attemptForDraft(shelf));
  const [sending, setSending] = useState(false);
  const [attachedApps, setAttachedApps] = useState<string[]>([]);
  const [focused, setFocused] = useState(false);
  const [fieldHeight, setFieldHeight] = useState(COMPOSER_ROW);

  const [touch] = useState(() => isTouchPointer());
  const [dictating, setDictating] = useState<Dictating>(QUIET);
  const [level, setLevel] = useState(0);
  const [builtDictation] = useState(browserDictation);
  const dictationPort = dictation ?? builtDictation;
  const field = useRef<InputHandle | null>(null);
  const picker = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (autoFocus || (focusSignal ?? 0) > 0) field.current?.focus();
  }, [autoFocus, focusSignal]);

  useEffect(() => () => dictationPort.discard?.(), [dictationPort]);

  const ready = (text.trim().length > 0 || picked.length > 0 || attachedApps.length > 0) && !busy && !sending;

  const typed = (next: string) => {
    revision.current += 1;
    if (next !== latest.current) {
      submission.current = null;
      forgetDraftAttempt(shelf);
    }
    setText(next);
    keepDraft(shelf, next);
    onDraft?.(next);
  };

  const [caret, setCaret] = useState(0);

  const room = useRef(shelf);
  if (room.current !== shelf) {
    revision.current += 1;
    room.current = shelf;
    submission.current = attemptForDraft(shelf);
    setText(draftFor(shelf));
    setCaret(0);
  }

  useEffect(() => {
    onDraft?.(draftFor(shelf));
  }, [shelf, onDraft]);

  const [put, setPut] = useState<{ start: number; end: number } | null>(null);
  useEffect(() => {
    if (put) setPut(null);
  }, [put]);
  const [active, setActive] = useState(0);

  const [shut, setShut] = useState<{ at: number; fragment: string } | null>(null);
  const [adding, setAdding] = useState<string | null>(null);
  const [wrong, setWrong] = useState<string | null>(null);

  const asking = whatIsBeingTyped(text, caret);

  const all = offersFrom(who, files, links);
  const offered = asking ? matchingTags(all, asking.fragment) : [];
  const dismissed =
    asking !== null && shut !== null && shut.at === asking.at && shut.fragment === asking.fragment;
  const open = focused && asking !== null && !dismissed && offered.length > 0;
  const chosen = offered[Math.min(active, offered.length - 1)];

  const write = (next: string, at: number) => {
    typed(next);
    setCaret(at);
    setPut({ start: at, end: at });
  };

  async function choose(one: Offer): Promise<void> {
    if (!asking) return;
    setWrong(null);

    const who = one.kind === "person" || one.kind === "agent" ? one.who : null;
    if (who && !who.here && onAdd) {
      setAdding(rowName(one));
      const said = await onAdd(who);
      setAdding(null);
      if (!said.added) {
        setWrong(said.why);
        return;
      }
    }
    const after = withTag(text, asking, one, caret);
    write(after.text, after.caret);
    setActive(0);
  }

  const send = async () => {
    if (!ready) return;
    const original = text;
    const sentShelf = shelf;
    const sentAtRevision = revision.current;
    const said = original.trim();
    const identifyBySignature = submissionIdentifier(submission.current, (attempt) => {
      keepDraftAttempt(sentShelf, attempt);
      if (room.current === sentShelf) submission.current = attempt;
    });
    const identify: SubmissionIdentity = (payload) => identifyBySignature(JSON.stringify(payload));
    setSending(true);
    let landed = false;
    try {
      landed = await onSay(said, originOf(field.current), identify, attachedApps);
    } catch {
      landed = false;
    } finally {
      setSending(false);
    }
    if (!landed) return;

    if (!submissionStillOwnsDraft(sentShelf, room.current, sentAtRevision, revision.current)) return;
    submission.current = null;
    forgetDraftAttempt(sentShelf);
    typed("");
    setAttachedApps([]);
    setCaret(0);
    setShut(null);
    onStopped?.();
  };

  const attach = onPick ? (
    <>
      <Tappable
        onPress={() => picker.current?.click()}
        accessibilityRole="button"
        accessibilityLabel={say("files.attach")}
        testID="attach"
        {...({ onMouseDown: (event: { preventDefault: () => void }) => event.preventDefault() } as object)}
        style={{
          width: COMPOSER_ROW,
          height: COMPOSER_ROW,
          borderRadius: 999,
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: theme.color["bg.surfaceAlt"],
        }}
      >
        <Icon name="plus" size={16} color="text.secondary" />
      </Tappable>
      <input
        ref={picker}
        type="file"
        multiple
        data-testid="attach-input"
        style={{ display: "none" }}
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          if (files.length > 0) onPick(files);
          event.target.value = "";
        }}
      />
    </>
  ) : null;

  const dictated = (said: string) => {
    if (!said) return;
    const after = withDictated(latest.current, said);
    write(after, after.length);
  };
  const dictate = async () => {
    if (dictating.recording) {
      const { now, said } = await pressedDictation(dictating, dictationPort);
      setDictating(now);
      setLevel(0);
      dictated(said);
      return;
    }
    const began = await dictationPort.start({
      onLevel: setLevel,
      onFinished: (said) => {
        setDictating(QUIET);
        setLevel(0);
        dictated(said);
      },
      onFailed: (why) => setDictating({ recording: false, why }),
    });
    setDictating(began.started ? { recording: true, why: null } : { recording: false, why: began.why });
  };

  const fieldInput = (
    <Input
      ref={field}
      framed={false}
      autoFocus={autoFocus}
      value={text}
      onChangeText={(next) => {
        typed(next);
        setCaret((was) => Math.max(0, was + next.length - text.length));
        setActive(0);
        setWrong(null);

        if (dictating.why) setDictating(QUIET);
        if (next.trim().length === 0) onStopped?.();
        else onTyped?.();
      }}
      selection={put ?? undefined}
      onSelectionChange={(event) => setCaret(event.nativeEvent.selection.end)}
      onContentSizeChange={(event) => {
        const measured = composerFieldHeight(event.nativeEvent.contentSize.height);
        setFieldHeight(latest.current.length === 0 ? COMPOSER_ROW : measured);
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      placeholder={placeholder ?? say("chat.write")}
      placeholderTextColor={theme.color["text.tertiary"]}
      multiline
      testID={testID}
      {...({
        onKeyPress: (event: KeyboardEvent) => {
          const means = whatTheKeyDoes(event, open);

          if (means === "newline" || means === "type") return;
          event.preventDefault();
          if (means === "send") {
            void send();
            return;
          }
          if (means === "dismiss") {
            setShut(asking);
            return;
          }
          if (means === "choose") {
            if (chosen) void choose(chosen);
            return;
          }
          const step = means === "next" ? 1 : offered.length - 1;
          setActive((was) => (Math.min(was, offered.length - 1) + step) % offered.length);
        },
      } as object)}
      style={
        {
          width: "100%",
          height: text.length === 0 ? COMPOSER_ROW : fieldHeight,
          minHeight: COMPOSER_ROW,
          maxHeight: COMPOSER_TALLEST,
          paddingHorizontal: 0,
          paddingVertical: Math.max(0, (COMPOSER_ROW - theme.type.body.line) / 2),
          color: theme.color["text.primary"],
          fontSize: theme.type.body.size,
          lineHeight: theme.type.body.line,
          outlineStyle: "none",
          ...grow,
        } as object
      }
    />
  );

  return (
    <Col
      style={{
        position: "relative",
        borderWidth: COMPOSER_BORDER,
        borderColor: focused ? theme.color.accent : theme.color["border.default"],
        backgroundColor: theme.color["bg.surface"],
        borderRadius: theme.radius.lg,
        paddingHorizontal: COMPOSER_INSET,
        paddingVertical: theme.space[2],
        gap: theme.space[2],

        overflow: "visible",
        zIndex: 5,
      }}
    >
      {replyContext ? (
        <Col
          style={{ flexDirection: "row", alignItems: "center", gap: theme.space[2] }}
          testID={`${testID}-reply`}
        >
          <Icon name="reply" size={14} color="text.tertiary" />
          <Text numberOfLines={1} style={{ flex: 1, color: theme.color["text.tertiary"], fontSize: 12 }}>
            {say("reply.to")(replyContext.said)}
          </Text>
          <Tappable
            onPress={replyContext.onClose}
            accessibilityRole="button"
            accessibilityLabel={say("reply.close")}
            testID={`${testID}-reply-close`}
          >
            <Icon name="close" size={14} color="text.tertiary" />
          </Tappable>
        </Col>
      ) : null}
      <Attached picked={picked} onDrop={(key) => onDrop?.(key)} />
      {error ? (
        <Text style={{ color: theme.color.danger, fontSize: 12 }} testID={`${testID}-error`}>
          {error}
        </Text>
      ) : null}
      {dictating.why ? (
        <Text style={{ color: theme.color["text.tertiary"], fontSize: 12 }} testID={`${testID}-dictation`}>
          {dictating.why}
        </Text>
      ) : null}
      <Col style={{ flexDirection: "row", alignItems: "flex-end", gap: theme.space[2] }}>
        <Col style={{ flex: 1, minWidth: 0 }}>{fieldInput}</Col>
        <Col
          testID={`${testID}-controls`}
          style={{ flexDirection: "row", alignItems: "center", gap: theme.space[2] }}
        >
          {attach}
          <AppAttachments
            apps={apps}
            selected={attachedApps}
            toggle={(id) =>
              setAttachedApps((held) =>
                held.includes(id) ? held.filter((one) => one !== id) : [...held, id],
              )
            }
          />
          {rightButton({
            has: text.trim().length > 0 || picked.length > 0 || attachedApps.length > 0,
            touch,
          }) === "send" ? (
            <SendButton ready={ready} onPress={() => void send()} testID={`${testID}-send`} />
          ) : (
            <Microphone
              recording={dictating.recording}
              level={level}
              onPress={() => void dictate()}
              testID={`${testID}-mic`}
            />
          )}
        </Col>
      </Col>

      {open ? (
        <MentionList
          offered={offered}
          active={Math.min(active, offered.length - 1)}
          onChoose={(one) => void choose(one)}
          busy={adding}
          wrong={wrong}
          conversation={conversation}
        />
      ) : null}
    </Col>
  );
}
